import React, { useState, useEffect, useRef } from "react";
import ModalSkeleton from "../../../Modals/ModalSkeleton";
import CustomButton from "../../../common/Button/CustomButton";
import { XIcon, DownloadIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";
import { apiCall } from "../../../../store/services/service";
import {
  URL_GET_FILE,
  URL_SIL_ACCOMMODATION_DOCUMENT_VIEW_FILE,
  URL_WORKER_GET_FILE,
} from "../../../../store/services/URL";
import { baseURLImage, sanitizeDocumentUrl } from "../../../../Utils/utils";
import { roleAccess } from "../../../../helpers/RolesPermission";
import { USER_ROLE } from "../../../../Constants/constant";
import { connect } from "react-redux";
import { getUser } from "../../../../store/selector/auth.selector";
import { toast } from "react-toastify";
import { lowerCase } from "lodash";
import Loader from "../../../common/Loader/Loader";

const FilePreviewModal = ({
  isOpen,
  onClose,
  fileItem,
  role = "participant",
  allFiles = [],
  currentLoginUser,
  /** Same as Folder: when "worker", non–admin/manager use HRM view-file for worker documents. */
  fileViewContext,
}) => {
  const [fileUrl, setFileUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState(null);
  const [showOfficeDocFallback, setShowOfficeDocFallback] = useState(false);
  const officeDocTimeoutRef = useRef(null);
  const lastFetchedFileRef = useRef(null);

  const currentFile = allFiles.length > 0 ? allFiles[currentIndex] : fileItem;

  useEffect(() => {
    if (isOpen && fileItem) {
      // Reset fallback when modal opens or file changes
      setShowOfficeDocFallback(false);
      // Find current file index in allFiles
      if (allFiles.length > 0) {
        const index = allFiles.findIndex(
          (f) => (f.uuid || `file-${f.id}`) === (fileItem.uuid || `file-${fileItem.id}`)
        );
        if (index !== -1) {
          setCurrentIndex(index);
        }
      }
      fetchFileUrl(fileItem);
    }
  }, [isOpen, fileItem, allFiles]);

  // Handle file navigation (Next/Previous buttons)
  useEffect(() => {
    // Only trigger on navigation when modal is open and we have files
    if (isOpen && allFiles.length > 0 && currentIndex >= 0) {
      const fileToFetch = allFiles[currentIndex];
      const fileKey = fileToFetch?.uuid || `file-${fileToFetch?.id}`;
      
      // Only fetch if this is a different file than the last one fetched
      if (fileToFetch && fileKey && fileKey !== lastFetchedFileRef.current) {
        // Clear previous file URL immediately to prevent showing old file
        setFileUrl(null);
        setError(null);
        setShowOfficeDocFallback(false);
        // Clear any pending timeouts
        if (officeDocTimeoutRef.current) {
          clearTimeout(officeDocTimeoutRef.current);
          officeDocTimeoutRef.current = null;
        }
        // Fetch new file
        fetchFileUrl(fileToFetch);
      }
    }
  }, [currentIndex, isOpen, allFiles]);

  // Reset fallback state when file changes and set timeout for Office docs only
  useEffect(() => {
    if (isOpen && currentFile && fileUrl) {
      // Always reset fallback state first
      setShowOfficeDocFallback(false);
      
      // Check extension directly to avoid dependency issues
      let ext = "";
      if (currentFile.file_type) {
        ext = currentFile.file_type.toLowerCase().trim().replace(/^\./, "");
      } else if (currentFile.name) {
        const nameMatch = currentFile.name.match(/\.([a-zA-Z0-9]+)$/);
        if (nameMatch && nameMatch[1]) {
          ext = nameMatch[1].toLowerCase().trim();
        }
      } else if (currentFile.description) {
        const descMatch = currentFile.description.match(/\.([a-zA-Z0-9]+)$/);
        if (descMatch && descMatch[1]) {
          ext = descMatch[1].toLowerCase().trim();
        } else {
          const parts = currentFile.description.split(".");
          if (parts.length > 1) {
            ext = parts.pop().toLowerCase().trim();
          }
        }
      }
      
      // Explicitly exclude videos, images, PDFs, and text files from Office document fallback
      const videoExtensions = ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv", "m4v"];
      const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"];
      const textExtensions = ["txt", "log", "md"];
      
      const isVideo = videoExtensions.includes(ext);
      const isImage = imageExtensions.includes(ext);
      const isPDF = ext === "pdf";
      const isText = textExtensions.includes(ext);
      
      // If it's not an Office document, ensure fallback is false and return early
      if (isVideo || isImage || isPDF || isText) {
        setShowOfficeDocFallback(false);
        return;
      }
      
      // Only set fallback for Office documents (excluding Excel which is handled separately)
      const isOfficeDoc = ["doc", "docx", "ppt", "pptx"].includes(ext);
      if (isOfficeDoc) {
        // Check if URL appears to be authenticated/not publicly accessible
        // URLs with "local_env", "/sp/", "/hrm/", or internal paths are likely not publicly accessible
        const isLikelyAuthenticated = fileUrl.includes("local_env") || 
                                      fileUrl.includes("/sp/") || 
                                      fileUrl.includes("/hrm/") ||
                                      fileUrl.includes("/participant/") ||
                                      fileUrl.includes("file-management");
        
        // For .doc files specifically, external viewers rarely work with authenticated URLs
        // Show fallback immediately only for .doc files
        // For .docx, .xlsx, .pptx - try preview first, then show fallback if it fails
        if (ext === "doc") {
          // .doc files almost never work with external viewers, show fallback immediately
          setTimeout(() => {
            setShowOfficeDocFallback(true);
          }, 100);
        } else {
          // For .docx, .xlsx, .pptx files - always try preview first
          // Clear any existing timeout
          if (officeDocTimeoutRef.current) {
            clearTimeout(officeDocTimeoutRef.current);
            officeDocTimeoutRef.current = null;
          }
          // Set timeout to show fallback only if preview doesn't load
          // The timeout will be cancelled if the iframe loads successfully
          officeDocTimeoutRef.current = setTimeout(() => {
            setShowOfficeDocFallback(true);
          }, 8000); // Give 8 seconds for preview to load before showing fallback
          return () => {
            if (officeDocTimeoutRef.current) {
              clearTimeout(officeDocTimeoutRef.current);
              officeDocTimeoutRef.current = null;
            }
          };
        }
      } else {
        // For non-Office documents (videos, images, PDFs, etc.), ensure fallback is always false
        setShowOfficeDocFallback(false);
      }
    } else {
      // Reset when modal closes or file changes
      setShowOfficeDocFallback(false);
    }
  }, [currentFile, isOpen, fileUrl]);

  const fetchFileUrl = async (file) => {
    if (!file) return;
    
    const fileKey = file.uuid || `file-${file.id}`;
    // Update ref to track which file we're fetching
    lastFetchedFileRef.current = fileKey;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const viewFileBaseUrl = (() => {
        if (role === "sil") {
          return URL_SIL_ACCOMMODATION_DOCUMENT_VIEW_FILE;
        }
        if (roleAccess([USER_ROLE.Sp_Standard_User])) {
          return URL_WORKER_GET_FILE;
        }
        if (fileViewContext === "worker") {
          if (roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])) {
            return URL_GET_FILE;
          }
          return URL_WORKER_GET_FILE;
        }
        return URL_GET_FILE;
      })();

      const response = await apiCall.get({
        url: `${viewFileBaseUrl}?file_management_uuid=${file.uuid}`,
        isAuthToken: currentLoginUser?.AccessToken,
        params: {},
      });

      // Only update state if this is still the file we want to display
      // (prevents race conditions if user navigates quickly)
      if (fileKey === lastFetchedFileRef.current) {
        if (response?.link) {
          setFileUrl(sanitizeDocumentUrl(baseURLImage(response.link)));
        } else {
          setError("File URL not found");
        }
      }
    } catch (err) {
      console.error("Error fetching file:", err);
      // Only set error if this is still the current file
      if (fileKey === lastFetchedFileRef.current) {
        setError("Failed to load file. Please try again.");
        toast.error("Failed to load file");
      }
    } finally {
      // Only update loading state if this is still the current file
      if (fileKey === lastFetchedFileRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleDownload = () => {
    if (fileUrl) {
      window.open(fileUrl, "_blank");
    }
  };

  const handlePrevious = () => {
    if (allFiles.length > 0 && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (allFiles.length > 0 && currentIndex < allFiles.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  /**
   * Comprehensive file extension detection
   * Checks multiple file fields in priority order to find the file extension
   * Priority: file_type -> name -> description -> link
   */
  const getFileExtension = () => {
    if (!currentFile) return "";
    
    let ext = "";
    
    // Priority 1: Check file_type field (most reliable)
    if (currentFile.file_type) {
      ext = currentFile.file_type.toLowerCase().trim().replace(/^\./, "");
      if (ext) return ext;
    }
    
    // Priority 2: Extract from name field using regex
    if (currentFile.name) {
      const nameMatch = currentFile.name.match(/\.([a-zA-Z0-9]+)$/);
      if (nameMatch && nameMatch[1]) {
        const potentialExt = nameMatch[1].toLowerCase().trim();
        // Validate: extension should be 1-5 chars, not just numbers
        if (potentialExt.length > 0 && potentialExt.length <= 5 && !/^\d+$/.test(potentialExt)) {
          ext = potentialExt;
          if (ext) return ext;
        }
      }
    }
    
    // Priority 3: Extract from description field using regex
    if (currentFile.description) {
      const descMatch = currentFile.description.match(/\.([a-zA-Z0-9]+)$/);
      if (descMatch && descMatch[1]) {
        const potentialExt = descMatch[1].toLowerCase().trim();
        // Validate: extension should be 1-5 chars, not just numbers
        if (potentialExt.length > 0 && potentialExt.length <= 5 && !/^\d+$/.test(potentialExt)) {
          ext = potentialExt;
          if (ext) return ext;
        }
      }
      // Fallback: split by dot (original logic)
      if (!ext) {
        const parts = currentFile.description.split(".");
        if (parts.length > 1) {
          const potentialExt = parts.pop().toLowerCase().trim();
          if (potentialExt.length > 0 && potentialExt.length <= 5 && !/^\d+$/.test(potentialExt)) {
            ext = potentialExt;
            if (ext) return ext;
          }
        }
      }
    }
    
    // Priority 4: Extract from link/url field if available using regex
    if (currentFile.link) {
      const linkMatch = currentFile.link.match(/\.([a-zA-Z0-9]+)(?:[?#]|$)/);
      if (linkMatch && linkMatch[1]) {
        const potentialExt = linkMatch[1].toLowerCase().trim();
        // Validate: extension should be 1-5 chars, not just numbers
        if (potentialExt.length > 0 && potentialExt.length <= 5 && !/^\d+$/.test(potentialExt)) {
          ext = potentialExt;
          if (ext) return ext;
        }
      }
    }
    
    return ext;
  };

  /**
   * File type detection functions
   */
  const isImage = () => {
    const ext = getFileExtension();
    return ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(ext);
  };

  const isPDF = () => {
    return getFileExtension() === "pdf";
  };

  const isVideo = () => {
    const ext = getFileExtension();
    return ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv", "m4v"].includes(ext);
  };

  const isOfficeDocument = () => {
    const ext = getFileExtension();
    return ["doc", "docx", "ppt", "pptx"].includes(ext);
  };

  const isTextFile = () => {
    const ext = getFileExtension();
    return ["txt", "log", "md"].includes(ext);
  };

  const isSpreadsheet = () => {
    const ext = getFileExtension();
    return ["csv", "xls", "xlsx"].includes(ext);
  };

  const modalFooter = () => (
    <div className='flex items-center justify-between w-full'>
      <div className='flex items-center gap-2'>
        {allFiles.length > 1 && (
          <>
            <CustomButton
              label='Previous'
              Icon={<ChevronLeftIcon className='h-4 w-4' />}
              clickHandler={handlePrevious}
              variant='secondary'
              isDisabled={currentIndex === 0}
            />
            <span className='text-sm text-gray-600 px-2'>
              {currentIndex + 1} of {allFiles.length}
            </span>
            <CustomButton
              label='Next'
              Icon={<ChevronRightIcon className='h-4 w-4' />}
              clickHandler={handleNext}
              variant='secondary'
              isDisabled={currentIndex === allFiles.length - 1}
            />
          </>
        )}
      </div>
      <div className='flex items-center gap-2'>
        <CustomButton
          label='Download'
          Icon={<DownloadIcon className='h-4 w-4' />}
          clickHandler={handleDownload}
          variant='primary'
          isDisabled={!fileUrl || isLoading}
        />
        <CustomButton
          label='Close'
          clickHandler={onClose}
          variant='secondary'
        />
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <ModalSkeleton
      title={currentFile?.description || "File Preview"}
      cssClass='h-[90vh] max-h-[90vh] min-h-0 w-[90%] max-w-6xl'
      bodyPadding='flex h-full flex-col p-0'
      stretchBody
      modalFooter={modalFooter()}
      isOpen={isOpen}
      closeModal={onClose}
    >
      <div className='flex flex-col flex-1 min-h-0 overflow-hidden mt-0'>
        {isLoading ? (
          <div className='flex-1 flex items-center justify-center w-full h-full min-h-[400px]'>
            <div className='flex flex-col items-center justify-center gap-4'>
              <Loader cssClass='text-blue-600 w-16 h-16' />
              <p className='text-gray-600 text-lg font-medium'>Loading file...</p>
            </div>
          </div>
        ) : error ? (
          <div className='flex-1 flex flex-col items-center justify-center text-gray-500 min-h-0'>
            <p className='text-lg font-semibold mb-2'>{error}</p>
            <CustomButton
              label='Try Again'
              clickHandler={() => fetchFileUrl(currentFile)}
              variant='primary'
            />
          </div>
        ) : fileUrl ? (
          <div className='flex-1 bg-gray-100 p-4 overflow-hidden min-h-0'>
            {isImage() ? (
              <div className='flex items-center justify-center h-full w-full bg-gray-50 min-h-0'>
                <img
                  key={currentFile?.uuid || currentFile?.id || fileUrl}
                  src={fileUrl}
                  alt={currentFile?.description || "Preview"}
                  className='max-w-full max-h-full object-contain rounded-lg shadow-lg'
                  onError={(e) => {
                    setError("Failed to load image. The file may be corrupted or inaccessible.");
                    e.target.style.display = 'none';
                  }}
                  style={{ maxHeight: 'calc(90vh - 200px)' }}
                />
              </div>
            ) : isPDF() ? (
              // Single viewer: direct PDF URL in iframe. Browser shows full doc with scroll (all pages).
              // No Google viewer / timeout / fallback — avoids same PDF sometimes all pages, sometimes first page only.
              <div className='flex h-full min-h-0 w-full flex-1 flex-col'>
                <iframe
                  key={`pdf-${currentFile?.uuid || currentFile?.id || fileUrl}`}
                  src={fileUrl}
                  className='min-h-0 w-full flex-1 border-0 rounded-lg bg-white shadow-lg'
                  title={currentFile?.description || "PDF Preview"}
                />
              </div>
            ) : isVideo() ? (
              <div className='flex items-center justify-center h-full w-full bg-black min-h-0'>
                <video
                  key={currentFile?.uuid || currentFile?.id || fileUrl}
                  src={fileUrl}
                  controls
                  className='max-w-full max-h-full rounded-lg'
                  style={{ maxHeight: 'calc(90vh - 200px)' }}
                  onError={(e) => {
                    setError("Failed to load video. The file may be corrupted or inaccessible.");
                    e.target.style.display = 'none';
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            ) : isOfficeDocument() ? (
              <div className='relative flex h-full min-h-0 w-full flex-1 flex-col'>
                {showOfficeDocFallback ? (
                  <div className='flex h-full w-full flex-1 items-center justify-center bg-gray-100'>
                    <div className='bg-white rounded-lg shadow-xl p-6 sm:p-8 max-w-2xl w-full mx-4 text-center'>
                      <p className='text-gray-700 mb-4 text-lg font-semibold'>
                        Preview Unavailable
                      </p>
                      <p className='text-sm text-gray-600 mb-4'>
                        Office document preview requires a publicly accessible URL. Since this file is protected, 
                        the external viewer cannot access it.
                      </p>
                      <p className='text-sm text-gray-500 mb-6'>
                        Please download the file to view it using Microsoft Office, Google Docs, or another compatible application.
                      </p>
                      <div className='text-sm text-gray-500 mb-6 bg-gray-50 p-4 rounded'>
                        <p className='break-words'><strong>File:</strong> {currentFile?.description || currentFile?.name || "Unknown"}</p>
                        <p><strong>Type:</strong> {getFileExtension().toUpperCase()}</p>
                      </div>
                      <div className='flex flex-col sm:flex-row gap-3 justify-center'>
                        <CustomButton
                          label='Download File'
                          Icon={<DownloadIcon className='h-4 w-4' />}
                          clickHandler={handleDownload}
                          variant='primary'
                        />
                        <CustomButton
                          label='Try Preview Again'
                          clickHandler={() => {
                            setShowOfficeDocFallback(false);
                            // Reset timeout to try again
                            setTimeout(() => {
                              setShowOfficeDocFallback(true);
                            }, 3000);
                          }}
                          variant='secondary'
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  (() => {
                    const ext = getFileExtension();
                    // External viewers (Google Docs, Office Online) require publicly accessible URLs
                    // Since our file URLs are authenticated, they may not work
                    const useGoogleDocsViewer = ext === "doc" || ext === "xls" || ext === "ppt";
                    const viewerUrl = useGoogleDocsViewer
                      ? `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`
                      : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
                    
                    return (
                      <iframe
                        key={currentFile?.uuid || currentFile?.id || fileUrl}
                        src={viewerUrl}
                        className='min-h-0 h-full w-full flex-1 border-0 rounded-lg bg-white shadow-lg'
                        title={currentFile?.description || "Office Document Preview"}
                        allow='fullscreen'
                        onLoad={() => {
                          // If iframe loads successfully, cancel the fallback timeout
                          if (officeDocTimeoutRef.current) {
                            clearTimeout(officeDocTimeoutRef.current);
                            officeDocTimeoutRef.current = null;
                            setShowOfficeDocFallback(false);
                          }
                        }}
                        onError={() => {
                          // If iframe fails to load, show fallback immediately
                          if (officeDocTimeoutRef.current) {
                            clearTimeout(officeDocTimeoutRef.current);
                            officeDocTimeoutRef.current = null;
                          }
                          setShowOfficeDocFallback(true);
                        }}
                      />
                    );
                  })()
                )}
              </div>
            ) : isSpreadsheet() ? (
              <div className='flex items-center justify-center h-full w-full bg-gray-50 min-h-0'>
                <div className='bg-white rounded-lg shadow-xl p-6 sm:p-8 max-w-2xl w-full mx-4 text-center'>
                  {/* Spreadsheet Icon */}
                  <div className='mb-4 flex justify-center'>
                    <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center'>
                      <svg className='w-8 h-8 text-green-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
                      </svg>
                    </div>
                  </div>
                  <p className='text-gray-700 mb-2 text-lg font-semibold'>
                    Spreadsheet Preview Not Available
                  </p>
                  <p className='text-sm text-gray-600 mb-4'>
                    {getFileExtension() === 'csv' 
                      ? 'CSV files cannot be previewed directly in the browser.'
                      : 'Excel files (.xls, .xlsx) cannot be previewed directly in the browser.'}
                  </p>
                  <p className='text-sm text-gray-500 mb-6'>
                    Please download the file to view it using Microsoft Excel, Google Sheets, or another spreadsheet application.
                  </p>
                  <div className='text-sm text-gray-500 mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200'>
                    <p className='break-words'><strong>File:</strong> {currentFile?.description || currentFile?.name || "Unknown"}</p>
                    <p className='mt-1'><strong>Type:</strong> {getFileExtension().toUpperCase()} Spreadsheet</p>
                  </div>
                  <CustomButton
                    label='Download File'
                    Icon={<DownloadIcon className='h-4 w-4' />}
                    clickHandler={handleDownload}
                    variant='primary'
                  />
                </div>
              </div>
            ) : isTextFile() ? (
              <div className='flex h-full min-h-0 w-full items-center justify-center bg-gray-50'>
                <div className='flex h-full min-h-0 w-full max-w-4xl flex-col overflow-auto rounded-lg bg-white p-6 shadow-lg'>
                  <iframe
                    key={currentFile?.uuid || currentFile?.id || fileUrl}
                    src={fileUrl}
                    className='min-h-0 w-full flex-1 border-0 rounded'
                    title={currentFile?.description || "Text File Preview"}
                    onError={() => {
                      setError("Failed to load text file. Click Download to view.");
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className='flex items-center justify-center h-full min-h-0'>
                <div className='bg-white rounded-lg shadow-lg p-8 max-w-2xl text-center'>
                  <p className='text-gray-600 mb-4 text-lg'>
                    Preview not available for this file type.
                  </p>
                  <p className='text-sm text-gray-500 mb-6'>
                    File type: <strong>{getFileExtension().toUpperCase() || "Unknown"}</strong>
                  </p>
                  <div className='text-sm text-gray-500 mb-4'>
                    <p><strong>File:</strong> {currentFile?.description || currentFile?.name || "Unknown"}</p>
                  </div>
                  <CustomButton
                    label='Download File'
                    Icon={<DownloadIcon className='h-4 w-4' />}
                    clickHandler={handleDownload}
                    variant='primary'
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className='flex-1 flex items-center justify-center text-gray-500 min-h-0'>
            <p>No file to preview</p>
          </div>
        )}
      </div>
    </ModalSkeleton>
  );
};

const mapStateToProps = (state) => {
  const currentLoginUser = getUser(state);
  return { currentLoginUser };
};

export default connect(mapStateToProps, null)(FilePreviewModal);

